import { useCallback, useMemo } from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { Button, Table, Text, VStack } from 'govwell-ui'
import { useTableState } from 'govwell-ui/components/Table/use-table-state'
import { AIChatbotFAQFragment } from 'types/graphql'

import CustomAnswerModal from 'src/components/AIAssistant/CustomAnswerModal'
import { useSearchAIChatbotFAQsQuery } from 'src/fetch/aiChatbotFAQ'
import useDisclosure from 'src/hooks/use-disclosure'
import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import AskedCountCell from 'src/pages/AIAssistantTrainPage/AskedCountCell'
import CustomAnswersTableButtons from 'src/pages/AIAssistantTrainPage/CustomAnswersTableButtons'
import ParsedHtmlMessage from 'src/pages/AIChat/AIResponseMessage/ParsedHtmlMessage'

type QuestionAndAnswerCellProps = {
  aiChatbotFAQ: AIChatbotFAQFragment
}

const QuestionAndAnswerCell = ({ aiChatbotFAQ }: QuestionAndAnswerCellProps) => {
  return (
    <VStack gap={1} align="start">
      <Text.BodyLarge className="line-clamp-2 font-semibold">
        {aiChatbotFAQ.question}
      </Text.BodyLarge>
      <ParsedHtmlMessage
        className="line-clamp-2 text-neutral-600"
        html={aiChatbotFAQ.answer ?? ''}
      />
    </VStack>
  )
}

const CustomAnswersTable = () => {
  const organizationId = useMyOrgId()
  const getRowId = useCallback((aiChatbotFAQ: AIChatbotFAQFragment) => `${aiChatbotFAQ.id}`, [])
  const { debouncedSearchQuery, ...tableProps } = useTableState<AIChatbotFAQFragment>()
  const { faqs, count, isFetching, refetch } = useSearchAIChatbotFAQsQuery({
    organizationId,
    pagination: {
      page: tableProps.pagination.pageIndex + 1,
      pageSize: tableProps.pagination.pageSize,
      query: debouncedSearchQuery,
    },
    type: 'Answered',
  })

  const createModalState = useDisclosure()

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<AIChatbotFAQFragment>()
    return [
      columnHelper.accessor((aiChatbotFAQ) => aiChatbotFAQ.count, {
        id: 'asked',
        header: '# Times Asked',
        cell: (info) => <AskedCountCell count={info.getValue()} />,
        enableSorting: false,
        meta: {
          width: '120px',
          textAlign: 'center',
        },
      }),
      columnHelper.display({
        id: 'questionAndAnswer',
        header: 'Question & Answer',
        cell: ({ row: { original: aiChatbotFAQ } }) => (
          <QuestionAndAnswerCell aiChatbotFAQ={aiChatbotFAQ} />
        ),
        enableSorting: false,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row: { original: aiChatbotFAQ } }) => (
          <CustomAnswersTableButtons aiChatbotFAQ={aiChatbotFAQ} refetch={refetch} />
        ),
        meta: {
          width: '80px',
          textAlign: 'center',
        },
      }),
    ]
  }, [refetch])

  return (
    <>
      <Table
        subtitle="Curated responses your assistant will always use. Sorted by most asked."
        actions={
          <Button icon="FaPlus" onClick={createModalState.open}>
            Create custom answer
          </Button>
        }
        columns={columns}
        data={faqs}
        enablePagination
        enableSearching
        searchInputPlaceholder="Search questions or answers..."
        getRowId={getRowId}
        isLoading={isFetching}
        totalCount={count}
        showCount
        emptyStateMessage="No custom answers yet. Create one to provide curated responses for common questions."
        {...tableProps}
      />
      {createModalState.isOpen && (
        <CustomAnswerModal modalType="create" onSubmit={refetch} onClose={createModalState.close} />
      )}
    </>
  )
}

export default React.memo(CustomAnswersTable)
